import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DescriptionEditDialog } from '@/components/ui-v2/product-description/description-edit-dialog';
import { SIDEBAR_WIDTH_PX } from '@/components/ui-v2/sidebar';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useToast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { useDealContext } from '@/lib/contexts/deal.context';
import {
  ActorRole,
  DealItem,
  DealItemUpdateInput,
  DealItemWithVersions,
  GetDealItemsDescriptionsDocument,
  type GetDealItemsDescriptionsQuery,
} from '@/lib/graphql/generated/graphql';
import { useExecuteOptimisticUpdate } from '@/lib/hooks/useExecuteOptimisticUpdate';
import { useTableDataReady } from '@/lib/hooks/useTableDataReady';
import { useUpdateDealItems } from '@/lib/hooks/useUpdateDealItems';
import { hasCriticalErrors, isProductModified } from '@/lib/utils';
import { isMaskableColumn } from '@/lib/utils/column-masking';
import { getBusinessColumnVisibility } from '@/lib/utils/column-visibility';
import { isFarmingBrand } from '@/lib/utils/deal-utils';
import { hasDescriptionFromText } from '@/lib/utils/has-description-from-text';
import { computeProductGroupKey } from '@/lib/utils/product-grouping';
import {
  findDealItemByVersionOrItemId,
  isDealItemCategoryUpdating,
  resolveDealItemFromMap,
} from '@/lib/utils/updating-fields';
import { hiddenSortableColumnsState } from '@/states/column-visibility-user.state';
import { dealIdentifierState } from '@/states/deal-identifier.state';
import { dealItemDescriptionsByActiveVersionIdState } from '@/states/deal-item-descriptions.state';
import { displayModeState } from '@/states/display-mode.state';
import { setEditingCellAtom } from '@/states/editing-cell.state';
import { hasCouponsInCurrentViewState } from '@/states/has-coupons-in-current-view.state';
import { hasDiscountedPriceOnDealAtom } from '@/states/has-discounted-price-on-deal.state';
import { isSidebarBulkEditOpenState } from '@/states/is-sidebar-bulk-edit-open.state';
import { isSidebarCompareOpen } from '@/states/is-sidebar-compare-open.state';
import { isSidebarFilterOpenState } from '@/states/is-sidebar-filter-open.state';
import { kpiComparisonState } from '@/states/kpi-comparison.state';
import { NO_CATEGORY_FILTER_KEY } from '@/states/product-category-filter.state';
import { productsMapState, productsState } from '@/states/products.state';
import { storeAtom } from '@/states/store.state';
import { undoMutationPendingState } from '@/states/undo-stack.state';
import { ColumnAccessor } from '@/types/columns';
import { useApolloClient } from '@apollo/client';
import { Row } from '@tanstack/react-table';
import { getDefaultStore, useAtomValue, useSetAtom } from 'jotai';
import { Loader2 } from 'lucide-react';

import { editionModeState } from '../../states/edition-mode.state';
import { hasShopifyIdsInCurrentViewState } from '../../states/has-shopify-ids-in-current-view.state';
import { FarmingBrandEmptyState } from '../farming-brand-empty-state';
import useColumnDefBestSeller from './columns/column-def-best-seller';
import useColumnDefBuyingPrice from './columns/column-def-buying-price';
import useColumnDefCheckbox from './columns/column-def-checkbox';
import useColumnDefChooseDiscount from './columns/column-def-choose-discount';
import useColumnDefChoosePrice from './columns/column-def-choose-price';
import useColumnDefCouponExpirationDate from './columns/column-def-coupon-expiration-date';
import useColumnDefCouponHowToUse from './columns/column-def-coupon-how-to-use';
import useColumnDefCouponTerms from './columns/column-def-coupon-terms';
import useColumnDefDescription from './columns/column-def-description';
import useColumnDefDiscountedPrice from './columns/column-def-discounted-price';
import useColumnDefEeePrice from './columns/column-def-eeeprice';
import useColumnDefGTIN from './columns/column-def-gtin';
import useColumnDefImages from './columns/column-def-images';
import useColumnDefListPrice from './columns/column-def-list-price';
import useColumnDefMadeIn from './columns/column-def-made-in';
import useColumnDefMarginPercentage from './columns/column-def-margin-percentage';
import useColumnDefName from './columns/column-def-name';
import useColumnDefPosition from './columns/column-def-position';
import useColumnDefPotentialSalesVolume from './columns/column-def-potential-sales-volume';
import useColumnDefType from './columns/column-def-product-category';
import useColumnDefProductType from './columns/column-def-product-type';
import useColumnDefShippingPrice from './columns/column-def-shipping-price';
import useColumnDefShopifyVariantId from './columns/column-def-shopify-variant-id';
import useColumnDefSKU from './columns/column-def-sku';
import useColumnDefStockQuantity from './columns/column-def-stock-quantity';
import useColumnDefURL from './columns/column-def-url';
import useColumnDefValidationErrors from './columns/column-def-validation-errors';
import useColumnDefVatRate from './columns/column-def-vat-rate';
import {
  ColumnVisibility,
  ExtendedColumnDef,
  TableProducts,
} from './table-products';

/**
 * Indique qu’il faut appeler l’API de descriptions lazy.
 * On ne teste que `cached === null` : dès que l’atom a été rempli une fois, on ne refetch pas
 * à chaque clic (évite les requêtes en boucle si un id présent côté client manque encore dans la map).
 * Les invalidations (subscription, import, etc.) repassent l’atom à `null` dans `useDealItems` quand il faut recharger.
 */
function dealItemDescriptionsNeedFetch(
  cached: Record<string, string | null> | null
): boolean {
  return cached === null;
}

function Table() {
  const { t } = useTranslation();

  const products = useAtomValue(productsState);
  const setProductsMap = useSetAtom(productsMapState);
  const setEditingCell = useSetAtom(setEditingCellAtom);
  const apolloClient = useApolloClient();
  const dealIdentifier = useAtomValue(dealIdentifierState);
  const displayMode = useAtomValue(displayModeState);
  const kpiComparison = useAtomValue(kpiComparisonState);
  const store = useAtomValue(storeAtom);
  const isBulkEditSidebarOpen = useAtomValue(isSidebarBulkEditOpenState);
  const isCompareSidebarOpen = useAtomValue(isSidebarCompareOpen);
  const isSidebarFilterOpen = useAtomValue(isSidebarFilterOpenState);
  const user = useUser();
  const editionMode = useAtomValue(editionModeState);
  const isOfferLocked = useIsOfferLocked();

  const { toast } = useToast();
  const undoPending = useAtomValue(undoMutationPendingState);
  const { updateProducts, canUndo, canRedo, undo, redo } =
    useUpdateDealItems(dealIdentifier);
  const { executeOptimisticUpdate } = useExecuteOptimisticUpdate();

  const { deal } = useDealContext();

  const { isReady, isLoading } = useTableDataReady(dealIdentifier);
  const hasCouponsInCurrentView = useAtomValue(hasCouponsInCurrentViewState);

  const hasAnyShopifyVariantId = useAtomValue(hasShopifyIdsInCurrentViewState);
  const hasDiscountedPrice = useAtomValue(hasDiscountedPriceOnDealAtom);
  const hiddenSortableColumnsRaw = useAtomValue(hiddenSortableColumnsState);
  const hiddenSortableColumns = useMemo(
    () => hiddenSortableColumnsRaw.filter(isMaskableColumn),
    [hiddenSortableColumnsRaw]
  );

  const skipSaveRef = useRef(false);

  const [descriptionModal, setDescriptionModal] = useState<{
    activeVersionId: string;
    isLeafRow: boolean;
    initialDraft: string;
    descriptionsReady: boolean;
  } | null>(null);

  const descriptionsFetchInFlightRef = useRef<Promise<boolean> | null>(null);

  const ensureDescriptionsLoaded = useCallback(async (): Promise<boolean> => {
    if (!dealIdentifier) return true;

    const jStore = getDefaultStore();
    const cached = jStore.get(dealItemDescriptionsByActiveVersionIdState);
    if (!dealItemDescriptionsNeedFetch(cached)) return true;

    const existing = descriptionsFetchInFlightRef.current;
    if (existing) return existing;

    const runFetch = async (): Promise<boolean> => {
      try {
        const { data } =
          await apolloClient.query<GetDealItemsDescriptionsQuery>({
            query: GetDealItemsDescriptionsDocument,
            variables: { dealId: dealIdentifier },
            fetchPolicy: 'network-only',
          });
        const items = data?.deal?.dealItemsWithVersions ?? [];
        const map: Record<string, string | null> = {};
        for (const item of items) {
          map[item.activeVersion.id] = item.activeVersion.description ?? null;
        }
        jStore.set(dealItemDescriptionsByActiveVersionIdState, map);
        setProductsMap((prev) => {
          const next = new Map(prev);
          for (const item of items) {
            const p = next.get(item.id);
            if (!p) continue;
            const descriptionText = item.activeVersion.description ?? null;
            next.set(item.id, {
              ...p,
              activeVersion: {
                ...p.activeVersion,
                description: descriptionText,
                hasDescription: hasDescriptionFromText(descriptionText),
              },
            });
          }
          return next;
        });
        return true;
      } catch (e) {
        console.error('GetDealItemsDescriptions failed', e);
        toast({
          title: t('general.error'),
          description: t('general.error_network_message'),
          variant: 'destructive',
        });
        return false;
      }
    };

    const fetchPromise = runFetch();
    descriptionsFetchInFlightRef.current = fetchPromise;
    void fetchPromise.finally(() => {
      if (descriptionsFetchInFlightRef.current === fetchPromise) {
        descriptionsFetchInFlightRef.current = null;
      }
    });
    return fetchPromise;
  }, [apolloClient, dealIdentifier, setProductsMap, t, toast]);

  const openDescriptionEditModal = useCallback(
    async (activeVersionId: string, isLeafRow: boolean) => {
      if (isOfferLocked) return;

      const productForDesc = findDealItemByVersionOrItemId(
        products,
        activeVersionId
      );
      const productsMap = getDefaultStore().get(productsMapState);
      const latestForDesc = productForDesc
        ? resolveDealItemFromMap(productsMap, productForDesc)
        : undefined;
      if (latestForDesc && isDealItemCategoryUpdating(latestForDesc)) return;

      const jStore = getDefaultStore();
      const cached = jStore.get(dealItemDescriptionsByActiveVersionIdState);

      if (!dealItemDescriptionsNeedFetch(cached)) {
        const map = jStore.get(dealItemDescriptionsByActiveVersionIdState);
        const raw =
          map?.[activeVersionId] ??
          productForDesc?.activeVersion.description ??
          null;
        setDescriptionModal({
          activeVersionId,
          isLeafRow,
          initialDraft: raw ?? '',
          descriptionsReady: true,
        });
        return;
      }

      setDescriptionModal({
        activeVersionId,
        isLeafRow,
        initialDraft: '',
        descriptionsReady: false,
      });

      const loaded = await ensureDescriptionsLoaded();
      if (!loaded) {
        setDescriptionModal(null);
        return;
      }

      const map = jStore.get(dealItemDescriptionsByActiveVersionIdState);
      const raw =
        map?.[activeVersionId] ??
        productForDesc?.activeVersion.description ??
        null;

      setDescriptionModal((prev) => {
        if (!prev || prev.activeVersionId !== activeVersionId) return prev;
        return {
          ...prev,
          initialDraft: raw ?? '',
          descriptionsReady: true,
        };
      });
    },
    [ensureDescriptionsLoaded, isOfferLocked, products]
  );

  const sidebarWidth = useMemo(() => {
    const hasAnySidebarOpen =
      isBulkEditSidebarOpen || isCompareSidebarOpen || isSidebarFilterOpen;
    return hasAnySidebarOpen ? SIDEBAR_WIDTH_PX : 0;
  }, [isBulkEditSidebarOpen, isCompareSidebarOpen, isSidebarFilterOpen]);

  const columnVisibility = useMemo(
    (): ColumnVisibility =>
      ({
        __displayMode: false,
        __searchTerms: false,
        __comparisonFilter: false,
        [ColumnAccessor._productGroupKey]: false,
        _checkbox: true,
        position: true,
        images: true,
        name: true,
        isBestSeller: true,
        ...getBusinessColumnVisibility({
          kpiComparison,
          hasCouponsInCurrentView,
          hasAnyShopifyVariantId,
          hasDiscountedPrice,
          store,
          isInternalUser: user?.isInternalUser ?? false,
          displayMode,
        }),
        ...Object.fromEntries(hiddenSortableColumns.map((col) => [col, false])),
      }) as ColumnVisibility,
    [
      displayMode,
      store,
      kpiComparison,
      hasCouponsInCurrentView,
      hasDiscountedPrice,
      user,
      hasAnyShopifyVariantId,
      hiddenSortableColumns,
    ]
  );

  const handleCellClick = useCallback(
    (id: string, fieldParam: string) => {
      void (async () => {
        const field = fieldParam as keyof DealItem;
        if (isOfferLocked) return;

        const product = findDealItemByVersionOrItemId(products, id);
        const productsMap = getDefaultStore().get(productsMapState);
        const latest = product
          ? resolveDealItemFromMap(productsMap, product)
          : undefined;
        if (latest && isDealItemCategoryUpdating(latest)) return;

        setEditingCell({ id: { id }, field });
      })();
    },
    [isOfferLocked, products, setEditingCell]
  );

  const exitEditing = useCallback(() => {
    setEditingCell(null);
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
    }
  }, [setEditingCell]);

  const handleUndo = useCallback(async () => {
    const success = await undo();
    if (success) {
      toast({
        title: t('general.success'),
        description: t('components.product_view.undo_success'),
      });
    } else {
      toast({
        title: t('general.error'),
        description: t('components.product_view.undo_error_conflict'),
        variant: 'destructive',
      });
    }
  }, [undo, toast, t]);

  const handleRedo = useCallback(async () => {
    const success = await redo();
    if (success) {
      toast({
        title: t('general.success'),
        description: t('components.product_view.redo_success'),
      });
    } else {
      toast({
        title: t('general.error'),
        description: t('components.product_view.redo_error_conflict'),
        variant: 'destructive',
      });
    }
  }, [redo, toast, t]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;

      const isUndo = e.key === 'z' && !e.shiftKey;
      const isRedo =
        e.key === 'y' ||
        (e.key === 'z' && e.shiftKey) ||
        (e.key === 'Z' && e.shiftKey);

      if (!isUndo && !isRedo) return;
      if (isOfferLocked || undoPending) return;
      if (isUndo && !canUndo) return;
      if (isRedo && !canRedo) return;

      // Ne pas interférer si une cellule est en cours d'édition
      const editingCell = document.activeElement?.closest('[data-editing]');
      if (editingCell) return;

      e.preventDefault();
      if (isUndo) void handleUndo();
      else void handleRedo();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [canUndo, canRedo, isOfferLocked, undoPending, handleUndo, handleRedo]);

  const performDealItemUpdate = async <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow: boolean = true
  ): Promise<boolean> => {
    if (!id) return false;

    // Columns pass activeVersion.id; support deal item id or "parent-"+id from made-in
    const searchId =
      typeof id === 'string' && id.startsWith('parent-') ? id.slice(7) : id;
    const product = findDealItemByVersionOrItemId(products, searchId);
    if (!product) return false;

    let productsToUpdate: DealItemWithVersions[] = [];
    if (!isLeafRow) {
      productsToUpdate = products.filter(
        (p) =>
          p.activeVersion.name === product.activeVersion.name &&
          p.activeVersion.isSelected
      );
    } else {
      productsToUpdate = [product];
    }

    const productsMap = getDefaultStore().get(productsMapState);
    if (
      productsToUpdate.some((p) =>
        isDealItemCategoryUpdating(resolveDealItemFromMap(productsMap, p))
      )
    )
      return false;

    const itemIds = productsToUpdate.map((p) => p.id);
    const itemsToSave: DealItemUpdateInput[] = productsToUpdate.map((pu) => ({
      id: pu.id,
      [field]: value,
    }));

    const productsToUpdateIds = new Set(productsToUpdate.map((u) => u.id));
    return await executeOptimisticUpdate({
      optimisticUpdate: (prev) => {
        if (field === 'description') {
          const dm = getDefaultStore().get(
            dealItemDescriptionsByActiveVersionIdState
          );
          if (dm !== null) {
            const updated = { ...dm };
            for (const pu of productsToUpdate) {
              updated[pu.activeVersion.id] =
                (value as string | null | undefined) ?? null;
            }
            getDefaultStore().set(
              dealItemDescriptionsByActiveVersionIdState,
              updated
            );
          }
        }
        return prev.map((p) => {
          if (!productsToUpdateIds.has(p.id)) return p;
          const nextActiveVersion = {
            ...p.activeVersion,
            [field as string]: value,
            ...(field === 'description' && {
              hasDescription: hasDescriptionFromText(
                value as string | null | undefined
              ),
            }),
            productGroupKey: computeProductGroupKey(
              field === 'name' ? (value as string) : p.activeVersion.name,
              field === 'isSelected'
                ? (value as boolean)
                : p.activeVersion.isSelected
            ),
          };
          return {
            ...p,
            activeVersion: nextActiveVersion,
          };
        });
      },
      mutation: () => updateProducts(itemsToSave),
      checkSuccess: (result) =>
        result.data?.updateDealItemsV2?.__typename ===
        'MutationUpdateDealItemsSuccess',
      getErrorMessage: (result) =>
        result.data?.updateDealItemsV2?.__typename === 'DomainException'
          ? result.data.updateDealItemsV2.code
          : t('general.error_cannot_save_modifications'),
      defaultErrorMessage: t('general.error_cannot_save_modifications'),
      errorContext: 'handleCellEdit',
      itemIds,
      modifiedFields: [field as string],
      onSuccess: () => {
        if (productsToUpdate.length > 1) {
          toast({
            title: t('general.success'),
            description:
              field === 'name'
                ? t('components.product_view.products_updated_variants_name', {
                    count: productsToUpdate.length,
                  })
                : t(
                    'components.product_view.products_updated_variants_included',
                    { count: productsToUpdate.length }
                  ),
          });
        }
      },
    });
  };

  const performDealItemUpdateRef = useRef(performDealItemUpdate);
  performDealItemUpdateRef.current = performDealItemUpdate;

  const handleCellEdit = useCallback(
    async <K extends keyof DealItem>(
      id: string | undefined,
      field: K,
      value: DealItem[K],
      isLeafRow: boolean = true
    ) => {
      await performDealItemUpdateRef.current(id, field, value, isLeafRow);
    },
    []
  );

  const handleBlur: React.FocusEventHandler<HTMLInputElement> =
    useCallback(() => {
      exitEditing();
    }, [exitEditing]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> =
    useCallback(
      (e) => {
        if (e.key === 'Escape') {
          skipSaveRef.current = true;
          setEditingCell(null);
          e.currentTarget.blur();
          return;
        }
        if (e.key === 'Enter') {
          skipSaveRef.current = false;
          setEditingCell(null);
          e.currentTarget.blur();
        }
      },
      [setEditingCell]
    );

  const handleVatMarginImpl = async (id: string) => {
    const product = findDealItemByVersionOrItemId(products, id);
    if (!product) return;
    const productsMap = getDefaultStore().get(productsMapState);
    if (
      isDealItemCategoryUpdating(resolveDealItemFromMap(productsMap, product))
    )
      return;

    const newHasVatMargin = !product.activeVersion.hasVatMargin;
    const payload = [{ id, hasVatMargin: newHasVatMargin }];

    await executeOptimisticUpdate({
      optimisticUpdate: (prev) =>
        prev.map((p) => {
          if (id !== p.id) return p;
          return {
            ...p,
            activeVersion: {
              ...p.activeVersion,
              hasVatMargin: newHasVatMargin,
            },
          };
        }),
      mutation: () => updateProducts(payload),
      checkSuccess: (result) =>
        result.data?.updateDealItemsV2?.__typename ===
        'MutationUpdateDealItemsSuccess',
      getErrorMessage: (result) =>
        result.data?.updateDealItemsV2?.__typename === 'DomainException'
          ? result.data.updateDealItemsV2.code
          : t('general.error_cannot_save_modifications'),
      defaultErrorMessage: t('general.error_cannot_save_modifications'),
      errorContext: 'handleVatMargin',
      itemIds: [id],
    });
  };
  const handleVatMarginRef = useRef(handleVatMarginImpl);
  handleVatMarginRef.current = handleVatMarginImpl;
  const handleVatMargin = useCallback(
    async (id: string) => handleVatMarginRef.current(id),
    []
  );

  const filterDisplayMode = (
    row: Row<DealItemWithVersions>,
    columnId: string,
    filterValue: string
  ) => {
    switch (filterValue) {
      case 'selected':
        return row.original.activeVersion.isSelected;
      case 'deselected':
        return !row.original.activeVersion.isSelected;
      case 'error':
        return (
          row.original.activeVersion.isSelected &&
          hasCriticalErrors(row.original.activeVersion.validationErrors)
        );
      case 'modified':
        return user?.role
          ? isProductModified(row.original, user.role, editionMode)
          : false;
      case 'modified_by_choose':
        return isProductModified(row.original, ActorRole.Brand, editionMode);
    }
    return false;
  };

  const filterSearchTerms = (
    row: Row<DealItemWithVersions>,
    columnId: string,
    filterValue: string
  ) =>
    row.original.activeVersion.name
      .toLowerCase()
      .includes(filterValue.toLowerCase()) ||
    row.original.activeVersion.sku
      ?.toLowerCase()
      .includes(filterValue.toLowerCase()) ||
    row.original.activeVersion.gtin
      ?.toLowerCase()
      .includes(filterValue.toLowerCase()) ||
    row.original.activeVersion.shopifyVariantId
      ?.toLowerCase()
      .includes(filterValue.toLowerCase()) ||
    false;

  const filterProductCategory = (
    row: Row<DealItemWithVersions>,
    columnId: string,
    filterValue: unknown
  ) => {
    if (!Array.isArray(filterValue) || filterValue.length === 0) {
      return true;
    }

    const productCategoryId =
      row.original.activeVersion.productCategory?.productCategoryId;

    if (!productCategoryId && filterValue.includes(NO_CATEGORY_FILTER_KEY)) {
      return true;
    }

    if (productCategoryId && filterValue.includes(productCategoryId)) {
      return true;
    }

    return false;
  };

  const filterComparison = (
    row: Row<DealItemWithVersions>,
    columnId: string,
    filterValue: unknown
  ) => {
    if (!Array.isArray(filterValue) || filterValue.length === 0) {
      return true;
    }

    return filterValue.includes(row.original.id);
  };

  const filterValidationErrors = (
    row: Row<DealItemWithVersions>,
    columnId: string,
    filterValue: unknown
  ) => {
    if (!Array.isArray(filterValue) || filterValue.length === 0) {
      return true;
    }

    const selectedRuleIds = new Set(
      filterValue.filter((id): id is string => typeof id === 'string')
    );

    const rowHasMatchingError = (item: DealItemWithVersions) =>
      item.activeVersion.validationErrors?.some((err) =>
        selectedRuleIds.has(err.ruleId)
      );

    if (row.subRows.length > 1) {
      return row.subRows.some((subRow) => rowHasMatchingError(subRow.original));
    }

    return rowHasMatchingError(row.original);
  };

  const prevProductsRef = useRef<DealItemWithVersions[]>([]);
  const dataMemo = useMemo(() => {
    const prev = prevProductsRef.current;
    if (
      products.length === prev.length &&
      products.every((p, i) => p === prev[i])
    ) {
      return prev;
    }
    prevProductsRef.current = products;
    return products;
  }, [products]);

  const columns: ExtendedColumnDef<DealItemWithVersions>[] = [
    {
      accessorKey: '__displayMode',
      filterFn: filterDisplayMode,
    },
    {
      accessorKey: '__searchTerms',
      filterFn: filterSearchTerms,
    },
    {
      accessorKey: '__comparisonFilter',
      filterFn: filterComparison,
    },
    {
      accessorKey: ColumnAccessor._productGroupKey,
      accessorFn: (row) => row.activeVersion?.productGroupKey ?? '',
      enableHiding: true,
    },
    useColumnDefCheckbox(),
    useColumnDefPosition({
      dealId: dealIdentifier,
      handleCellClick,
    }),
    useColumnDefImages(),
    useColumnDefName({
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    }),

    useColumnDefBestSeller(),
    useColumnDefStockQuantity({
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    }),
    useColumnDefGTIN({
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    }),
    useColumnDefSKU({
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    }),
    useColumnDefShopifyVariantId(),
    useColumnDefListPrice({
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    }),
    useColumnDefDiscountedPrice({
      handleCellClick,
      handleCellEdit,
      handleBlur,
      handleKeyDown,
    }),
    useColumnDefChooseDiscount({
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    }),
    useColumnDefChoosePrice({
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    }),
    useColumnDefBuyingPrice({
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    }),
    useColumnDefMarginPercentage({
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    }),
    useColumnDefCouponExpirationDate({
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
      exitEditing,
    }),
    useColumnDefCouponTerms(),
    useColumnDefCouponHowToUse(),
    useColumnDefVatRate({
      handleVatMargin,
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    }),
    useColumnDefPotentialSalesVolume(),
    useColumnDefMadeIn({
      handleCellClick,
      handleCellEdit,
    }),
    useColumnDefShippingPrice({
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    }),
    useColumnDefType({
      dealId: dealIdentifier,
      filterFn: filterProductCategory,
    }),
    useColumnDefDescription({
      openDescriptionEditModal,
    }),
    useColumnDefURL({
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    }),
    useColumnDefEeePrice({
      handleCellClick,
      handleCellEdit,
      handleBlur,
      handleKeyDown,
    }),

    useColumnDefProductType({
      handleCellEdit,
    }),
    { ...useColumnDefValidationErrors(), filterFn: filterValidationErrors },
  ];

  const hasProducts = products.length > 0;

  const displayFarmingBrandEmptyState =
    isFarmingBrand(deal?.latestPreviousCampaigns) &&
    !hasProducts &&
    user?.isInternalUser;

  if (isLoading || !isReady) {
    return (
      <div className="flex h-full w-full items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (displayFarmingBrandEmptyState) {
    return (
      <FarmingBrandEmptyState
        dealId={dealIdentifier}
        campaigns={deal?.latestPreviousCampaigns ?? []}
      />
    );
  }

  return (
    <>
      <DescriptionEditDialog
        open={descriptionModal !== null}
        onOpenChange={(open) => {
          if (!open) setDescriptionModal(null);
        }}
        initialValue={descriptionModal?.initialDraft ?? ''}
        descriptionsReady={descriptionModal?.descriptionsReady ?? true}
        onSave={async (value) => {
          if (!descriptionModal) return false;
          return performDealItemUpdate(
            descriptionModal.activeVersionId,
            'description',
            value,
            descriptionModal.isLeafRow
          );
        }}
        disabled={isOfferLocked}
      />
      <TableProducts
        data={dataMemo}
        columns={columns}
        columnVisibility={columnVisibility}
        sidebarWidth={sidebarWidth}
      />
    </>
  );
}

export default Table;
